import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircle, Phone, Clock } from "lucide-react";
import { useState } from "react";

export default function ContactSupportPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "general",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log("Support request submitted:", formData);
    setSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: "",
        email: "",
        subject: "",
        category: "general",
        message: "",
      });
    }, 3000);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Contact Support</h1>
          <p className="text-gray-200 mt-2">
            We're here to help. Reach out to our support team.
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Contact Options */}
        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-6 h-6 text-[var(--secondary)]" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Live Chat</h3>
            <p className="text-sm text-gray-600 mb-4">
              Chat with our support team in real-time
            </p>
            <button className="text-[var(--secondary)] font-medium text-sm hover:underline">
              Start Chat
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Email Support</h3>
            <p className="text-sm text-gray-600 mb-4">
              Get help via email within 24 hours
            </p>
            <a
              href="mailto:support@airarrange.com"
              className="text-green-600 font-medium text-sm hover:underline"
            >
              support@airarrange.com
            </a>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Phone Support</h3>
            <p className="text-sm text-gray-600 mb-4">
              Speak directly with our team
            </p>
            <a
              href="tel:+1234567890"
              className="text-purple-600 font-medium text-sm hover:underline"
            >
              +1 (234) 567-890
            </a>
          </div>
        </section>

        {/* Support Hours */}
        <section className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-[var(--secondary)] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-lg mb-3">Support Hours</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-1">Live Chat & Phone</p>
                  <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                  <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM EST</p>
                  <p className="text-gray-600">Sunday: Closed</p>
                </div>
                <div>
                  <p className="font-medium mb-1">Email Support</p>
                  <p className="text-gray-600">24/7 - We respond within 24 hours</p>
                  <p className="text-gray-600 mt-2">
                    Priority support available for urgent issues
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-6">Send us a message</h2>

          {submitted ? (
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <MessageCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-sm text-green-800">
                    Thank you for contacting us. Our support team will get back
                    to you within 24 hours.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent"
                >
                  <option value="general">General Inquiry</option>
                  <option value="booking">Booking Issue</option>
                  <option value="payment">Payment & Billing</option>
                  <option value="technical">Technical Support</option>
                  <option value="hosting">Hosting Question</option>
                  <option value="account">Account Management</option>
                  <option value="safety">Safety & Trust</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent"
                  placeholder="Brief description of your issue"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent resize-none"
                  placeholder="Please provide as much detail as possible about your inquiry..."
                />
              </div>

              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  <strong>Tip:</strong> For faster assistance, include relevant
                  booking IDs, error messages, or screenshots if applicable.
                </p>
              </div>

              <button
                type="submit"
                className="w-full bg-[var(--secondary)] text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
              >
                Send Message
              </button>
            </form>
          )}
        </section>

        {/* Quick Links */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold mb-6">
            Before you contact us...
          </h2>
          <p className="text-gray-700 mb-4">
            You might find your answer faster in our help resources:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/help/faq"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-[var(--secondary)]" />
              </div>
              <div>
                <h3 className="font-semibold">FAQs</h3>
                <p className="text-sm text-gray-600">
                  Common questions and answers
                </p>
              </div>
            </Link>

            <Link
              to="/help/getting-started"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Getting Started</h3>
                <p className="text-sm text-gray-600">
                  New to AirArrange? Start here
                </p>
              </div>
            </Link>
          </div>
        </section>

        {/* Emergency Contact */}
        <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
          <h3 className="font-semibold text-red-900 mb-2">
            Emergency or Safety Issue?
          </h3>
          <p className="text-sm text-red-800 mb-3">
            If you're experiencing a safety emergency at a workspace, contact
            local emergency services immediately (911 in US). Then notify us at:
          </p>
          <a
            href="tel:+1234567890"
            className="inline-flex items-center gap-2 text-red-900 font-semibold hover:underline"
          >
            <Phone className="w-4 h-4" />
            Emergency Hotline: +1 (234) 567-890
          </a>
          <p className="text-xs text-red-700 mt-2">Available 24/7</p>
        </div>
      </main>
    </div>
  );
}
